import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { type z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApiNoConversion } from '~/services/kyClient';

import {
  type ContactPoint,
  ContactPointObject,
} from '~/models/dataSubscriptions';

import { queryKeysDataSubscription } from './queryKeys';
import { contactPointFromApi } from './userKeyedFields';
import { zodRawContactPointListSchema } from './validations';

type ContactPointItem = unknown;

/**
 * Fetches a list of contact points from the API.
 * This function retrieves contact points data and optionally maps them into instances of `ContactPointObject`
 * if 'initContactPoint' is true.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/get_all_contact_points_data_subscription_contact_point_get
 * @returns {Promise<ContactPointResponse>} Response from the server.
 * @throws {Error} If the API request fails.
 */
export const fetchContactPoints = async (initContactPoint = true) => {
  // A response that fails validation is logged and passed through as is.
  const response = await vestigasApiNoConversion
    .get(ENDPOINT.DATA_SUBSCRIPTION.GET_CONTACT_POINTS(), {
      validate: { response: zodRawContactPointListSchema },
    })
    .json<z.input<typeof zodRawContactPointListSchema>>();

  const contactPoints = (response.contact_points ?? []).map((item) =>
    contactPointFromApi(item),
  );

  if (initContactPoint) {
    return contactPoints.map((item) => ContactPointObject.create(item));
  }

  return contactPoints;
};

/**
 * Custom React Query hook to fetch contact points.
 */
export const useQueryContactPoints = (
  initContactPoint = true,
  options?: Omit<
    UseQueryOptions<ContactPointItem[] | ContactPoint[]>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    queryFn: async () => fetchContactPoints(initContactPoint),
    queryKey: queryKeysDataSubscription.getAllContactPoints(initContactPoint),
    ...options,
  });
